import { FunctionComponent, useEffect, useState, useRef } from 'react';
import cx from 'classnames';
import hoistStatics from 'hoist-non-react-statics';
import styles from './WithFocusRing.module.css';

const incrementables: any = {
  radio: true,
};

const ARROW_KEYS: any = {
  37: true,
  38: true,
  39: true,
  40: true,
};

const TAB_KEY = 9;

type WithFocusRingProps = {
  wrappedComponentRef: any;
  focusRingClass?: string;
  extraFocusRingClass?: string;
  className?: string;
  onKeyUp: any;
  onBlur: any;
  type?: string;
};

export default function WithFocusRing(ComponentToWrap: any, staticProps: any = {}) {
  const WithFocusRingInner: FunctionComponent<WithFocusRingProps> = props => {
    const {
      focusRingClass: focusRingClassProps,
      extraFocusRingClass: extraFocusRingClassProps,
      className,
      onKeyUp: onKeyUpProp,
      onBlur: onBlurProp,
    } = props;

    const { focusRingClass: staticFocusRingClass, extraFocusRingClass: staticExtraFocusRingClass } =
      staticProps;

    const [showFocusRing, setShowFocusRing] = useState(false);
    const [focusRingClass] = useState(
      focusRingClassProps || staticFocusRingClass || styles.showFocusRing
    );
    const [extraFocusRingClass] = useState<string>(
      staticExtraFocusRingClass || extraFocusRingClassProps
    );
    const node = useRef<any>(undefined);

    // biome-ignore lint/correctness/useExhaustiveDependencies: intentional dependency management
    useEffect(() => {
      window.addEventListener('focus', onWindowFocus);
      if (node.current) node.current?.addEventListener('blur', onNativeBlur);
      return () => {
        window.removeEventListener('focus', onWindowFocus);
        if (node.current) node.current?.removeEventListener('blur', onNativeBlur);
      };
    }, []);

    const onWindowFocus = () => {
      if (document.activeElement === node.current) {
        setShowFocusRing(true);
      }
    };

    const onNativeBlur = () => {
      setShowFocusRing(false);
    };

    const onKeyUp = (event: any) => {
      if (shouldHandleKeyUpEvent(event)) {
        setShowFocusRing(true);
      }
      if (typeof onKeyUpProp === 'function') {
        onKeyUpProp(event);
      }
    };

    const onBlur = (event: any) => {
      setShowFocusRing(false);
      if (typeof onBlurProp === 'function') {
        onBlurProp(event);
      }
    };

    const getNode = (nodeElement: any) => {
      node.current = nodeElement;

      if (typeof props.wrappedComponentRef === 'function') {
        props.wrappedComponentRef(node);
      }
    };

    const componentProps = { ...props };

    delete componentProps.focusRingClass;
    delete componentProps.extraFocusRingClass;
    delete componentProps.wrappedComponentRef;

    return (
      <ComponentToWrap
        {...componentProps}
        ref={(refNode: any) => {
          getNode(refNode);
        }}
        onKeyUp={onKeyUp}
        onBlur={onBlur}
        className={cx(className, styles.container, {
          [focusRingClass]: showFocusRing,
          [extraFocusRingClass]: showFocusRing,
        })}
      />
    );
  };

  return hoistStatics<any, any>(WithFocusRingInner, ComponentToWrap);
}

function shouldHandleKeyUpEvent({ keyCode, target }: { keyCode: number; target: any }) {
  const isTab = keyCode === TAB_KEY;
  const useArrowKeys = incrementables[target.type] && ARROW_KEYS[keyCode];

  return isTab || useArrowKeys;
}
